import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from '../../useTranslation'
import useDebouncedEffect from '../../common/hooks/useDebouncedEffect'

interface TwoFactorAuthenticationProps {
    onChange: (value: string) => void
    onResendCode: () => void
    error: string
}

const TwoFactorAuthentication = ({ onChange, onResendCode, error }: TwoFactorAuthenticationProps) => {
    const [isResending, setIsResending] = useState(false)
    const [counter, setCounter] = useState(0)
    const [digits, setDigits] = useState(['', '', '', '', '', ''])
    const { t } = useTranslation()
    const inputRefs = useRef<HTMLInputElement[]>([])

    useDebouncedEffect(
        () => {
            inputRefs.current[0]?.focus()
        },
        500,
        []
    )

    const isDigitValid = (digit: string) => {
        return digit != null && digit.length === 1 && digit.trim() != '' && /^[0-9]*$/.test(digit)
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target

        setDigits((prevDigits) => {
            const updatedDigits = [...prevDigits]
            updatedDigits[index] = isDigitValid(value) ? value : ''
            return updatedDigits
        })

        if (isDigitValid(value)) {
            if (index < inputRefs.current.length - 1) {
                inputRefs.current[index + 1]?.focus()
            }
        }
    }

    useEffect(() => {
        if (digits.filter((digit) => isDigitValid(digit)).length === 6) {
            onChange(digits.join(''))
        }
    }, [digits])

    const handleResendCode = () => {
        if (!isResending) {
            onResendCode()
            setIsResending(true)
            setCounter(120)
        }
    }

    useEffect(() => {
        if (isResending && counter > 0) {
            const timer = setTimeout(() => setCounter(counter - 1), 1000)
            return () => clearTimeout(timer)
        } else {
            setIsResending(false)
        }
    }, [isResending, counter])

    const handlePaste = (e: { clipboardData: { getData: (arg0: string) => string } }) => {
        const pastedToken = e.clipboardData.getData('text').slice(0, 6).padEnd(6, ' ')
        setDigits(pastedToken.split(''))
    }

    const inputBoxes = digits.map((digit, index) => (
        <input
            key={index}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onPaste={handlePaste}
            onChange={(e) => handleChange(e, index)}
            className={`text-4xl w-[72px] h-[72px] border ${
                error ? 'border-red-600' : 'border-gray-200'
            } text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none outline-hidden`}
            ref={(el) => {
                inputRefs.current[index] = el as HTMLInputElement
            }}
        />
    ))

    return (
        <div className="w-full">
            <div className="flex gap-1 ">{inputBoxes}</div>
            {error && (
                <div className="text-red-600 text-left text-base pt-2">
                    <FontAwesomeIcon icon={['fas', 'circle-info']} className="pr-1" />
                    {error}
                </div>
            )}
            {isResending && <p className="text-left text-base pt-2 ">{t('login.2fa.codeWasResent')}</p>}
            <p className="text-left py-[38px] text-base">
                {t('login.2fa.haventReceived2faCode')}{' '}
                <span
                    className={`pl-1 text-custom_yellow font-bold ${isResending ? 'cursor-not-allowed opacity-50' : 'hover:underline cursor-pointer'}`}
                    onClick={handleResendCode}
                >
                    {t('login.2fa.sendAgain')}
                    {isResending && ` ${counter} s`}
                </span>
            </p>
        </div>
    )
}

export default TwoFactorAuthentication
